import BTInputField from "@common-components/bt-input-field/bt-input-field";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { setExtensionSidebarSearchToken } from "@common/reducers/WhatsAppReducer";
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Stack, IconButton } from "@mui/material";
import CalendarSidebarFilters from "./calendar-sidebar-filters";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEffect, useRef, useState } from "react";

export default function ExtensionSidebarSearchBox() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const searchToken = useAppSelector((state) => state.WhatsAppReducer.extensionSidebarSearchToken);

    // Keep the raw input in local state and debounce the store dispatch so we
    // don't re-run every sidebar selector on each keystroke.
    const [localToken, setLocalToken] = useState('');
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

    // Sync local state when the store token changes (e.g. external reset).
    useEffect(() => {
        setLocalToken(searchToken ?? '');
    }, [searchToken]);

    useEffect(() => {
        dispatch(setExtensionSidebarSearchToken(''));
        return () => {
            clearTimeout(debounceTimerRef.current);
            dispatch(setExtensionSidebarSearchToken(''));
        };
    }, [dispatch]);

    const handleChange = (value: string) => {
        setLocalToken(value);
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            dispatch(setExtensionSidebarSearchToken(value));
        }, 250);
    };

    return (
        <Stack sx={{
            padding: '10px',
            borderBottom: '1px solid var(--color-border)',
            alignItems: 'center'
        }}>
            <BTInputField
                placeholder={t('sidebar.searchScheduledMessages')}
                value={localToken}
                onChange={(event) => handleChange(event.target.value)}
                startAdornment={<SearchIcon sx={{ fontSize: "20px", color: 'var(--color-text)', marginLeft: '5px' }} />}
                endAdornment={
                    <Popover>
                        <PopoverTrigger asChild>
                            <IconButton
                                size="small"
                                aria-label={t('calendarSidebarFilters.title', { defaultValue: 'Filters' })}
                                sx={{
                                    color: 'var(--color-text)',
                                    '&:hover': {
                                        backgroundColor: 'var(--color-surface-muted)',
                                    }
                                }}
                            >
                                <FilterAltOutlinedIcon sx={{ fontSize: "20px" }} />
                            </IconButton>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-[318px] p-0 overflow-hidden">
                            <CalendarSidebarFilters />
                        </PopoverContent>
                    </Popover>
                }
                sx={{
                    width: '100%',
                    padding: '0px 4px',
                    "&.Mui-focused": {
                        ".MuiOutlinedInput-notchedOutline": {
                            border: "1px solid var(--color-border)",
                            boxShadow: 'none',
                        },
                    },
                    "&: hover": {
                        ".MuiOutlinedInput-notchedOutline": {
                            border: "1px solid var(--color-border)",
                            boxShadow: 'none',
                        },
                    },
                }}
            />
        </Stack>
    );
}
